import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Session, User } from '@supabase/supabase-js';
import { AUTH_STORAGE_KEY, supabase } from '@/services/supabaseClient';
import { authService } from '@/services/authService';
import { geolocationService } from '@/services/geolocationService';
import { subscriptionService } from '@/services/subscriptionService';
import { FREE_SHARED_SLOT_LIMIT } from '@/services/slotLimitService';
import type { Profile, UserPlan } from '@/types/database';

export const useUserStore = defineStore('user', () => {
  type LocationSource = 'gps' | 'address' | null;
  type SwipeLocationResolution = {
    status: 'ready' | 'blocked';
    source: LocationSource;
  };

  const user = ref<User | null>(null);
  const session = ref<Session | null>(null);
  const profile = ref<Profile | null>(null);
  const plan = ref<UserPlan | null>(null);
  const loading = ref(false);
  const locationSource = ref<LocationSource>(null);
  let authChangeUnsubscribe: (() => void) | null = null;
  let swipeLocationResolvePromise: Promise<SwipeLocationResolution> | null = null;

  const isAuthenticated = computed(() => !!user.value);
  const isPro = computed(() => plan.value?.tier === 'pro');
  const inventoryLimit = computed(() => {
    if (!user.value || !plan.value) return null;
    if (isPro.value) return null;
    const planLimit = plan.value?.inventory_slots;
    if (planLimit == null) return FREE_SHARED_SLOT_LIMIT;
    return Math.min(Math.max(1, Math.trunc(Number(planLimit))), FREE_SHARED_SLOT_LIMIT);
  });
  const maxRadiusKm = computed(() => plan.value?.max_radius_km ?? 100);

  type InitOptions = {
    blockingUserData?: boolean;
  };

  function extractSessionFromStoredPayload(raw: string | null): Session | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const directUser = parsed?.user as Record<string, unknown> | undefined;
      if (directUser?.id) return parsed as unknown as Session;

      // Backward/alternate payload shape used by some auth-js flows.
      const currentSession = parsed?.currentSession as Record<string, unknown> | undefined;
      const currentUser = currentSession?.user as Record<string, unknown> | undefined;
      if (currentUser?.id) return currentSession as unknown as Session;
    } catch {
      return null;
    }
    return null;
  }

  function hydrateSession() {
    // Read the cached session directly from localStorage — never call
    // supabase.auth.getSession() here. That call acquires the auth-js v2
    // internal lock; if it runs slowly (cold start, PKCE exchange, token
    // refresh) the lock is held and any subsequent signInWithPassword call
    // blocks indefinitely, making the login button appear stuck.
    // The INITIAL_SESSION event from onAuthStateChange (registered below,
    // before this runs) will update session/user with the validated token
    // once the Supabase client finishes its own async initialization.
    try {
      const restoredSession = extractSessionFromStoredPayload(
        window.localStorage.getItem(AUTH_STORAGE_KEY)
      );
      if (restoredSession?.user?.id) {
        session.value = restoredSession as Session;
        user.value = restoredSession.user as User;
      }
    } catch {
      // Storage corrupt or unavailable — stay logged out
    }
  }

  async function preloadUserData() {
    if (!user.value) {
      profile.value = null;
      plan.value = null;
      locationSource.value = null;
      return;
    }
    console.debug('[preloadUserData] caricamento dati utente', user.value.id);
    try {
      await Promise.all([fetchProfile(), fetchPlan()]);
      console.debug('[preloadUserData] completato — profile:', !!profile.value, 'plan:', plan.value?.tier ?? 'null');
    } catch (err) {
      console.error('[preloadUserData] fallito:', err);
      throw err;
    }
  }

  async function init(options: InitOptions = {}) {
    const { blockingUserData = true } = options;
    if (loading.value) return;
    loading.value = true;
    try {
      // Register the auth change listener BEFORE hydrating so the
      // INITIAL_SESSION event is never missed if the Supabase client
      // initializes before the first await below.
      if (!authChangeUnsubscribe) {
        const { data: { subscription } } = authService.onAuthChange((_event, newSession: any) => {
          const prevUserId = user.value?.id ?? null;
          session.value = newSession;
          user.value = newSession?.user ?? null;
          if (!user.value) {
            profile.value = null;
            plan.value = null;
            locationSource.value = null;
          } else if (user.value.id !== prevUserId) {
            // User ID changed: new sign-in or INITIAL_SESSION restoring a session
            // that hydrateSession() missed. Schedule outside the auth lock scope.
            // TOKEN_REFRESHED / USER_UPDATED keep the same ID → no redundant calls.
            setTimeout(() => {
              void preloadUserData().catch((err) => {
                console.error('Error loading user data after auth change:', err);
              });
            }, 0);
          }
        });
        authChangeUnsubscribe = () => subscription.unsubscribe();
      }

      hydrateSession();

      if (user.value) {
        const preloadPromise = preloadUserData();
        if (blockingUserData) {
          await preloadPromise;
        } else {
          void preloadPromise.catch((err) => {
            console.error('Error preloading user data:', err);
          });
        }
      }
    } finally {
      loading.value = false;
    }
  }

  async function fetchProfile() {
    if (!user.value) return;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.value.id)
      .maybeSingle();
    if (error) {
      console.error('[fetchProfile] errore query profiles:', error);
      throw error;
    }
    if (!data) {
      console.warn('[fetchProfile] nessun profilo trovato per', user.value.id);
      return;
    }
    profile.value = data as Profile;
  }

  async function fetchPlan() {
    if (!user.value) return;
    try {
      plan.value = await subscriptionService.myPlan(user.value.id);
    } catch (err) {
      console.error('[fetchPlan] errore query v_user_plan:', err);
      throw err;
    }
  }

  async function updateProfile(patch: Partial<Profile>) {
    if (!user.value) throw new Error('Non autenticato');
    const { data, error } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', user.value.id)
      .select()
      .single();
    if (error) throw error;
    profile.value = data as Profile;
  }

  async function refreshLocation() {
    const resolved = await resolveLocationForSwipe();
    if (resolved.status !== 'ready') {
      throw new Error('location_unavailable');
    }
  }

  async function saveAddressLocation(lat: number, lon: number, postalCode?: string, countryCode?: string) {
    await geolocationService.saveMyLocation(lat, lon, postalCode, countryCode);
    locationSource.value = 'address';
    await fetchProfile();
  }

  async function resolveLocationForSwipe(): Promise<SwipeLocationResolution> {
    if (!user.value) {
      locationSource.value = null;
      return { status: 'blocked', source: null };
    }

    if (swipeLocationResolvePromise) return swipeLocationResolvePromise;

    const resolvePromise: Promise<SwipeLocationResolution> = (async (): Promise<SwipeLocationResolution> => {
      if (!profile.value) {
        await fetchProfile().catch(() => { });
      }

      try {
        // GPS is always the first priority, even when profile.location is already set.
        await geolocationService.refreshAndSave();
        locationSource.value = 'gps';
        await fetchProfile().catch(() => { });
        return { status: 'ready', source: 'gps' };
      } catch (gpsError) {
        console.warn('[resolveLocationForSwipe] GPS unavailable, trying profile.location fallback', gpsError);
      }

      await fetchProfile().catch(() => { });
      if (profile.value?.location) {
        locationSource.value = 'address';
        return { status: 'ready', source: 'address' };
      }

      locationSource.value = null;
      return { status: 'blocked', source: null };
    })();

    swipeLocationResolvePromise = resolvePromise.finally(() => {
      swipeLocationResolvePromise = null;
    });

    return resolvePromise;
  }

  async function signIn(email: string, password: string) {
    const data = await authService.signIn(email, password);
    // signInWithPassword() holds the auth lock for its entire scope and fires
    // onAuthStateChange synchronously before returning. The callback above detects
    // the new user ID and schedules preloadUserData() via setTimeout — outside the
    // lock scope. We set session/user here too for immediate reactivity, in case
    // the callback's reactive update hasn't propagated yet.
    session.value = data.session;
    user.value = data.user;
    // preloadUserData() is triggered by the onAuthStateChange callback; no second
    // call needed here.
  }

  async function signUp(
    email: string,
    password: string,
    username: string,
    extras?: {
      first_name?: string;
      last_name?: string;
      street_address?: string;
      city?: string;
      region?: string;
      postal_code?: string;
      country_code?: string;
    }
  ) {
    await authService.signUp({ email, password, username, ...extras });
  }

  async function signInWithGoogle() {
    await authService.signInWithGoogle();
    // OAuth flow redirects the browser — session will be set via onAuthChange after redirect
  }

  async function signInWithFacebook() {
    await authService.signInWithFacebook();
    // OAuth flow redirects the browser — session will be set via onAuthChange after redirect
  }

  async function signOut() {
    await authService.signOut();
    user.value = null;
    profile.value = null;
    plan.value = null;
    session.value = null;
    locationSource.value = null;
  }

  return {
    user, session, profile, plan, loading, locationSource,
    isAuthenticated, isPro, inventoryLimit, maxRadiusKm,
    init, fetchProfile, fetchPlan, updateProfile, refreshLocation, saveAddressLocation, resolveLocationForSwipe,
    signIn, signUp, signInWithGoogle, signInWithFacebook, signOut,
  };
});
